
import { computed, defineComponent, ref } from 'vue';
import { templateRef, useIntersectionObserver, useMediaQuery } from '@vueuse/core';
import BCXMarkdown from '@/components/molecules/BCXMarkdown.vue';
import X from '@/components/svg-components/X.vue';
import useI18n from '@/mixins/useI18n';
import useRouterIgnoreDuplicate from '@/mixins/useRouterIngoreDuplicate';
import useResponsiveness from '@/mixins/useResponsiveness';
import useProfilePdfUploadPopup from '@/views/profile/composables/useProfilePdfUploadPopup';
import usePageState from '@/state/pageState';
import ProfileImage from '@/assets/img/info-popups/cheatsheet_icon_profile_2x.png';
import InsiderImage from '@/assets/img/info-popups/cheatsheet_icon_insider_gold_2x.png';
import ProjectsImage from '@/assets/img/info-popups/cheatsheet_icon_projects_2x.png';
import RecommendationsImage from '@/assets/img/info-popups/cheatsheet_icon_recommendations_gold_2x.png';
import ReputationImage from '@/assets/img/info-popups/cheatsheet_icon_reputation_2x.png';
import MyProjectsImage from '@/assets/img/info-popups/cheatsheet_icon_myprojects_2x.png';

export default defineComponent({
  name: 'InfoPopupCheatsheet',
  components: { BCXMarkdown, X },
  emits: ['close', 'hide'],
  setup(_, { emit }) {
    const { t } = useI18n();
    const { pushRoute } = useRouterIgnoreDuplicate();
    const { isSmartphoneWidth } = useResponsiveness();
    const hasFinePointer = useMediaQuery('(hover: hover) and (pointer: fine)');
    const { globalErrorMessage } = usePageState();
    const {
      requestOpenOnProfileVisit,
      clearOpenOnProfileVisit,
    } = useProfilePdfUploadPopup();
    const popup = templateRef<HTMLElement>('popup');
    const isInsiderGlowActive = ref(false);
    const insiderVisibilityThreshold = isSmartphoneWidth.value ? 0 : 0.8;
    const activatesInsiderGlowOnPointerMove = hasFinePointer.value
      && !isSmartphoneWidth.value;
    const insiderGlowBounds = ref<{ width: number; height: number } | null>(null);
    const insiderCopyFrame = computed(() => popup.value?.querySelector<HTMLElement>(
      '[data-card="insider"] .c-card-copy-frame',
    ));
    const insiderGlowViewBox = computed(() => {
      if (!insiderGlowBounds.value) return '0 0 0 0';
      const { width, height } = insiderGlowBounds.value;
      return `0 0 ${width} ${height}`;
    });
    const insiderGlowLeftPath = computed(() => {
      if (!insiderGlowBounds.value) return '';
      const { width, height } = insiderGlowBounds.value;
      const radius = Math.min(15, width / 2, height / 2);
      return `M ${width / 2} ${height} H ${radius} Q 0 ${height} 0 ${height - radius}`
        + ` V ${radius} Q 0 0 ${radius} 0 H ${width / 2}`;
    });
    const insiderGlowRightPath = computed(() => {
      if (!insiderGlowBounds.value) return '';
      const { width, height } = insiderGlowBounds.value;
      const radius = Math.min(15, width / 2, height / 2);
      return `M ${width / 2} ${height} H ${width - radius}`
        + ` Q ${width} ${height} ${width} ${height - radius}`
        + ` V ${radius} Q ${width} 0 ${width - radius} 0 H ${width / 2}`;
    });
    const activateInsiderGlow = (bounds: Pick<DOMRectReadOnly, 'width' | 'height'>) => {
      insiderGlowBounds.value = { width: bounds.width, height: bounds.height };
      isInsiderGlowActive.value = true;
    };
    const { stop: stopInsiderVisibilityObserver } = useIntersectionObserver(
      insiderCopyFrame,
      ([entry]) => {
        if (activatesInsiderGlowOnPointerMove) return;
        const isVisible = insiderVisibilityThreshold === 0
          ? entry.isIntersecting
          : entry.intersectionRatio >= insiderVisibilityThreshold;
        if (!isVisible) return;
        activateInsiderGlow(entry.boundingClientRect);
        stopInsiderVisibilityObserver();
      },
      { threshold: insiderVisibilityThreshold },
    );
    const activateInsiderGlowOnPointerMove = () => {
      if (!activatesInsiderGlowOnPointerMove || isInsiderGlowActive.value) return;
      const bounds = insiderCopyFrame.value?.getBoundingClientRect();
      if (!bounds) return;
      activateInsiderGlow(bounds);
      stopInsiderVisibilityObserver();
    };

    const cards = [
      {
        key: 'profile', image: ProfileImage, width: 90, height: 91
      },
      {
        key: 'insider', image: InsiderImage, width: 90, height: 91
      },
      {
        key: 'recommendations',
        image: RecommendationsImage,
        width: 90,
        height: 92,
      },
      {
        key: 'projects', image: ProjectsImage, width: 90, height: 91
      },
      {
        key: 'reputation', image: ReputationImage, width: 90, height: 91
      },
      {
        key: 'my-projects', image: MyProjectsImage, width: 90, height: 91
      },
    ];

    const showNavigationError = () => {
      globalErrorMessage.value = {
        code: 'navigation',
        i18Key: 'common.unknownError',
        component: 'InfoPopupCheatsheet',
        withoutReload: true,
      };
    };

    const openProfileAssistant = async () => {
      requestOpenOnProfileVisit();
      emit('hide');
      try {
        await pushRoute({ name: 'profile' });
      } catch (error) {
        clearOpenOnProfileVisit();
        showNavigationError();
      }
    };

    return {
      activateInsiderGlowOnPointerMove,
      cards,
      emit,
      insiderGlowLeftPath,
      insiderGlowRightPath,
      insiderGlowViewBox,
      isInsiderGlowActive,
      openProfileAssistant,
      showNavigationError,
      t,
    };
  },
});
